import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLanguage } from "../context/LanguageContext";
import { LAUNCH_DATE } from "../config";

function getTimeLeft() {
  const diff = LAUNCH_DATE.getTime() - Date.now();
  const clamped = Math.max(0, diff);
  return {
    days: Math.floor(clamped / (1000 * 60 * 60 * 24)),
    hours: Math.floor((clamped / (1000 * 60 * 60)) % 24),
    minutes: Math.floor((clamped / (1000 * 60)) % 60),
    seconds: Math.floor((clamped / 1000) % 60),
  };
}

const pad = (n) => String(n).padStart(2, "0");

const FlipNumber = ({ value }) => (
  <div className="relative h-[1em] overflow-hidden tabular-nums">
    <AnimatePresence mode="popLayout" initial={false}>
      <motion.span
        key={value}
        initial={{ y: "100%" }}
        animate={{ y: "0%" }}
        exit={{ y: "-100%" }}
        transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
        className="block"
      >
        {value}
      </motion.span>
    </AnimatePresence>
  </div>
);

const Unit = ({ value, label, testKey, index }) => (
  <motion.div
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.6, delay: index * 0.08, ease: [0.16, 1, 0.3, 1] }}
    className="group relative flex flex-1 flex-col items-center justify-center px-2 py-10 md:py-16"
    data-testid={`countdown-${testKey}`}
  >
    <span className="pointer-events-none absolute left-2 top-2 font-mono-x text-[10px] text-muted-x">
      {pad(index + 1)}
    </span>
    <span
      className="font-mono-x text-5xl font-extrabold leading-none tracking-tighter sm:text-7xl lg:text-8xl"
      style={{ color: "var(--xp-primary)" }}
    >
      <FlipNumber value={pad(value)} />
    </span>
    <span className="mt-4 font-mono-x text-[10px] uppercase tracking-[0.3em] text-muted-x sm:text-xs">
      {label}
    </span>
  </motion.div>
);

export const Countdown = () => {
  const { t } = useLanguage();
  const [time, setTime] = useState(getTimeLeft());

  useEffect(() => {
    const id = setInterval(() => setTime(getTimeLeft()), 1000);
    return () => clearInterval(id);
  }, []);

  const launchStr = LAUNCH_DATE.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: "Asia/Jakarta",
  });

  const units = [
    { value: time.days, label: t.countdown.days, key: "days" },
    { value: time.hours, label: t.countdown.hours, key: "hours" },
    { value: time.minutes, label: t.countdown.minutes, key: "minutes" },
    { value: time.seconds, label: t.countdown.seconds, key: "seconds" },
  ];

  return (
    <section
      id="countdown"
      className="relative border-b bp-border"
      data-testid="countdown-section"
    >
      <div className="mx-auto max-w-7xl px-4 md:px-8">
        <div className="flex flex-wrap items-end justify-between gap-3 border-b bp-border py-6">
          <div>
            <p className="font-mono-x text-[10px] uppercase tracking-[0.3em] text-muted-x">
              T-MINUS // {t.countdown.title}
            </p>
            <h2 className="mt-2 font-display text-3xl uppercase tracking-tight sm:text-4xl lg:text-5xl">
              {t.countdown.subtitle}
            </h2>
          </div>
          <p className="font-mono-x text-xs uppercase tracking-[0.2em] text-muted-x">
            {t.countdown.launchOn}{" "}
            <span style={{ color: "var(--xp-primary)" }}>{launchStr}</span>
          </p>
        </div>

        <div className="grid grid-cols-2 divide-x divide-y bp-border border-x border-b bp-border md:grid-cols-4 md:divide-y-0">
          {units.map((u, i) => (
            <Unit
              key={u.key}
              value={u.value}
              label={u.label}
              testKey={u.key}
              index={i}
            />
          ))}
        </div>
      </div>
    </section>
  );
};
